"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutoDrawing = void 0;
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const gsap_1 = __importDefault(require("gsap"));
const rxjs_1 = require("rxjs");
class AutoDrawing extends pixi_js_1.Graphics {
    constructor(strokes, speed = 0.035, strokecolor = 0xf44336, strokewidth = 4) {
        super();
        this.strokes = strokes;
        this.speed = speed;
        this.strokecolor = strokecolor;
        this.strokewidth = strokewidth;
        this.stroked = new rxjs_1.Subject();
        this.tmatrix = (pixi_js_1.Matrix.IDENTITY).scale((0, Global_1.px)(0.34), (0, Global_1.px)(0.34));
        this.main = gsap_1.default.timeline({
            autoRemoveChildren: true,
            onComplete: () => {
                this.stroked.complete();
            }
        });
        this.blurFilter = new pixi_js_1.BlurFilter((0, Global_1.px)(5), 8);
        this.w = 0;
        this.h = 0;
        this.filters = [this.blurFilter];
        this.setupTimeline();
    }
    get width() {
        return (0, Global_1.px)(this.w);
    }
    get height() {
        return (0, Global_1.px)(this.h);
    }
    get timeline() {
        return this.main;
    }
    destroy(_options) {
        this.main.kill();
        this.main = null;
        this.blurFilter.destroy();
        super.destroy(_options);
    }
    setupTimeline() {
        var _a, _b;
        for (let k = 0; k < this.strokes.length; k++) {
            const s = this.strokes[k]; // For now test with just stroke 0
            let pastindex;
            let pprev;
            let mmine;
            const tl = gsap_1.default.timeline({
                autoRemoveChildren: true,
                onComplete: () => {
                    this.stroked.next(k);
                },
                onStart: () => {
                    pastindex = 0;
                    pprev = { x: null, y: null };
                    mmine = { x: null, y: null };
                },
                onUpdate: () => {
                    var _a;
                    // This curve setting controls how strong the smoothing should be
                    // Careful! Setting this too high may lead to strange corners!
                    const curved = (x1, y1, x2, y2) => {
                        // const d = this.distance(x1, y1, x2, y2)/10;
                        // console.log(this.distance(x1, y1, x2, y2));
                        // return d < 1 ? 0 : d;
                        return 0;
                    };
                    this.clear();
                    if (typeof this.strokecolor === 'number') {
                        this.lineStyle((0, Global_1.px)(this.strokewidth), this.strokecolor);
                    }
                    else { // Texture stroke
                        this.lineStyle({
                            width: (0, Global_1.px)(this.strokewidth),
                            texture: this.strokecolor,
                            matrix: this.tmatrix
                        });
                    }
                    // Draw our past strokes
                    for (let v = 0; v < k; v++) {
                        const ss = this.strokes[v];
                        this.moveTo((0, Global_1.px)(ss[0][0]), (0, Global_1.px)(ss[1][0]));
                        for (let j = 1; j < ((_a = ss[0]) === null || _a === void 0 ? void 0 : _a.length); j++) {
                            const c = curved(ss[0][j - 1], ss[1][j - 1], ss[0][j], ss[1][j]);
                            this.arcTo((0, Global_1.px)(ss[0][j - 1]), (0, Global_1.px)(ss[1][j - 1]), (0, Global_1.px)(ss[0][j]), (0, Global_1.px)(ss[1][j]), (0, Global_1.px)(c));
                        }
                    }
                    // Draw our past points
                    this.moveTo((0, Global_1.px)(s[0][0]), (0, Global_1.px)(s[1][0]));
                    for (let j = 1; j < pastindex + 2; j++) {
                        const c = curved(s[0][j - 1], s[1][j - 1], s[0][j], s[1][j]);
                        this.arcTo((0, Global_1.px)(s[0][j - 1]), (0, Global_1.px)(s[1][j - 1]), (0, Global_1.px)(s[0][j]), (0, Global_1.px)(s[1][j]), (0, Global_1.px)(c));
                    }
                    // Draw our cur animating point
                    this.moveTo((0, Global_1.px)(s[0][pastindex]), (0, Global_1.px)(s[1][pastindex]));
                    if (mmine && pprev && mmine.x !== null && mmine.y !== null && pprev.x !== null && pprev.y !== null) {
                        // Draw an arc "smoothing" previous
                        const c = curved(pprev.x, pprev.y, mmine.x, mmine.y);
                        this.arcTo((0, Global_1.px)(pprev.x), (0, Global_1.px)(pprev.y), (0, Global_1.px)(mmine.x), (0, Global_1.px)(mmine.y), 0);
                    }
                    else if (mmine && mmine.x !== null && mmine.y !== null) {
                        // Just connect to next line
                        this.lineTo((0, Global_1.px)(mmine.x), (0, Global_1.px)(mmine.y));
                    }
                }
            });
            if (((_a = s[0]) === null || _a === void 0 ? void 0 : _a.length) === ((_b = s[1]) === null || _b === void 0 ? void 0 : _b.length)) {
                // TODO: What happens if there are less then three points?
                for (let i = 1; i < s[0].length - 1; i++) {
                    const mine = { x: s[0][i], y: s[1][i] };
                    const next = { x: s[0][i + 1], y: s[1][i + 1] };
                    const prev = { x: s[0][i - 1], y: s[1][i - 1] };
                    const distance = this.distance(prev.x, prev.y, mine.x, mine.y);
                    // Calc width/height
                    this.w = Math.max(this.w, mine.x, next.x, prev.x);
                    this.h = Math.max(this.h, mine.y, next.y, prev.y);
                    const duration = distance * this.speed;
                    tl.to(mine, {
                        x: next.x,
                        y: next.y,
                        ease: 'none',
                        duration: duration,
                        onComplete: () => {
                            pastindex = i;
                        },
                        onUpdate: () => {
                            if (mmine) {
                                mmine.x = mine.x;
                                mmine.y = mine.y;
                            }
                        }
                    }).to(prev, {
                        x: mine.x,
                        y: mine.y,
                        ease: 'none',
                        duration: duration,
                        onUpdate: () => {
                            if (pprev) {
                                pprev.x = prev.x;
                                pprev.y = prev.y;
                            }
                        }
                    }, '<');
                }
            }
            this.main.add(tl);
        }
    }
    distance(x1, y1, x2, y2) {
        return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    }
    // Just to get Typescript to stop wining :(
    setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY) {
        return super.setTransform(x, y, scaleX, scaleY, rotation, skewX, skewY, pivotX, pivotY);
    }
}
exports.AutoDrawing = AutoDrawing;
